import { effect } from 'kerfjs';

import {
  closeChatStream,
  invalidatePendingDemandOpen,
  loadDemands,
  loadLibrary,
  loadOrgSavings,
  loadProjectChats,
  loadQuoteMatrix,
  loadSettings,
  loadVendors,
  openDemand,
  openProjectChat,
  openVendor,
  syncLibraryChatTarget,
} from '../data/loaders.js';
import {
  chatActiveId,
  chatConversations,
  chatMessages,
  chatProjectId,
  chatTarget,
  documentPreview,
  editorSegment,
  libraryProjects,
  libraryScope,
  libraryTab,
  notice,
  settingsPage,
  settingsTab,
  status,
  summaryPane,
  vendorDetail,
  view,
  workspace,
} from '../data/state.js';
import { type AppRoute, appRouteUrl, parseAppRoute } from './app-route.js';
import { canOpenDemandSegment, currentCapabilities, currentDemandId } from './context.js';
import { createHistoryRestoreState } from './history-restore-state.js';
import { settingsPageFor } from './settings-nav.js';

let cancelPendingRestore: (() => void) | null = null;

/** Keep a user-initiated view change from being folded into an in-flight Back/Forward entry. */
export function cancelAppHistoryRestore(): void {
  cancelPendingRestore?.();
}

function currentAppRoute(): AppRoute {
  switch (view.value) {
    case 'workspace':
    case 'intake': {
      const id = currentDemandId();
      return id === undefined
        ? { view: 'demands' }
        : { view: 'demand', id, section: editorSegment.value, pane: summaryPane.value };
    }
    case 'conversations': {
      const id = chatActiveId.value;
      return (chatTarget.value?.scope === 'project' || chatTarget.value?.scope === 'organization') &&
        chatConversations.value.some((item) => item.id === id)
        ? { view: 'chat', id }
        : { view: 'chat' };
    }
    case 'savings':
      return { view: 'savings' };
    case 'settings':
      return { view: 'settings', area: settingsTab.value, page: settingsPage.value };
    case 'library':
      return { view: 'library', scope: libraryScope.value, tab: libraryTab.value };
    case 'vendors':
      return vendorDetail.value === null ? { view: 'vendors' } : { view: 'vendors', id: vendorDetail.value.vendor.id };
    default:
      return { view: 'demands' };
  }
}

/** A Back/Forward step the page may hold: `proceed` follows it, `stay` returns to the page. */
export interface HistoryNavigation {
  proceed: () => void;
  stay: () => void;
}

/**
 * Keep screen navigation in history and restore direct/shared URLs without a reload.
 * `hold` may keep a Back/Forward step from leaving a page with unsaved edits (HS2-HV4R7C).
 */
export function wireAppHistory({ hold }: { hold?: (navigation: HistoryNavigation) => boolean } = {}): void {
  let started = false;
  const restoreState = createHistoryRestoreState();
  let syncQueued = false;
  cancelPendingRestore = () => {
    restoreState.cancel();
  };

  const restore = async (): Promise<void> => {
    if (status.value !== 'ready') return;
    const generation = restoreState.begin();
    const route = parseAppRoute(window.location.search);
    documentPreview.value = null;
    if (route.view !== 'demand') invalidatePendingDemandOpen();
    if (route.view !== 'chat' && route.view !== 'library') closeChatStream();
    const staff = currentCapabilities().organizationSettings;
    switch (route.view) {
      case 'demands':
        view.value = 'demands';
        workspace.value = null;
        await loadDemands({ shareInFlight: true });
        break;
      case 'demand':
        summaryPane.value = route.pane;
        await openDemand(route.id);
        if (!restoreState.isCurrent(generation)) return;
        if (currentDemandId() !== route.id) {
          view.value = 'demands';
          workspace.value = null;
          notice.value = 'Demand unavailable.';
          break;
        }
        editorSegment.value = canOpenDemandSegment(route.section) ? route.section : 'lines';
        if (editorSegment.value === 'quotes') void loadQuoteMatrix(route.id);
        break;
      case 'chat':
        if (!staff) {
          view.value = 'demands';
          break;
        }
        view.value = 'conversations';
        await loadProjectChats();
        if (!restoreState.isCurrent(generation)) return;
        if (route.id === undefined) {
          chatActiveId.value = '';
          chatMessages.value = [];
          chatTarget.value = chatProjectId.value === '' ? null : { scope: 'project', projectId: chatProjectId.value };
          closeChatStream();
        } else if (chatConversations.value.some((item) => item.id === route.id)) await openProjectChat(route.id);
        break;
      case 'savings':
        view.value = staff ? 'savings' : 'demands';
        if (staff) await loadOrgSavings();
        break;
      case 'settings': {
        const area = staff ? route.area : 'personal';
        const page = settingsPageFor(area, route.page);
        settingsTab.value = area;
        settingsPage.value = page;
        if (area === 'organization' && page === 'knowledge-base') {
          libraryScope.value = '';
          libraryTab.value = 'documents';
          view.value = 'library';
          await loadLibrary();
          void syncLibraryChatTarget();
        } else if (area === 'organization' && page === 'vendors') {
          view.value = 'vendors';
          vendorDetail.value = null;
          await loadVendors();
        } else {
          view.value = 'settings';
          if (staff) await loadSettings();
        }
        break;
      }
      case 'library':
        if (!staff) {
          view.value = 'demands';
          break;
        }
        settingsTab.value = route.scope === '' ? 'organization' : 'project';
        settingsPage.value = 'knowledge-base';
        libraryScope.value = route.scope;
        libraryTab.value = route.tab;
        view.value = 'library';
        await loadLibrary();
        if (!restoreState.isCurrent(generation)) return;
        if (route.scope !== '' && !libraryProjects.value.some((project) => project.id === route.scope)) {
          libraryScope.value = '';
          settingsTab.value = 'organization';
          await loadLibrary();
        }
        void syncLibraryChatTarget();
        break;
      case 'vendors':
        if (!staff) {
          view.value = 'demands';
          break;
        }
        view.value = 'vendors';
        vendorDetail.value = null;
        await loadVendors();
        if (!restoreState.isCurrent(generation)) return;
        if (route.id !== undefined) await openVendor(route.id);
        break;
    }
    if (!restoreState.finish(generation)) return;
    const canonical = appRouteUrl(currentAppRoute());
    if (window.location.pathname + window.location.search !== canonical)
      window.history.replaceState(null, '', canonical);
  };

  window.addEventListener('popstate', () => {
    // The browser has already moved the URL; staying puts the page's URL back on top.
    const leaving = appRouteUrl(currentAppRoute());
    const held =
      status.value === 'ready' &&
      hold?.({
        proceed: () => void restore(),
        stay: () => {
          window.history.pushState(null, '', leaving);
        },
      }) === true;
    if (!held) void restore();
  });
  effect(() => {
    currentAppRoute(); // subscribe to the route's reactive inputs
    const ready = status.value === 'ready';
    if (!ready) return;
    if (!started) {
      started = true;
      void restore();
      return;
    }
    if (restoreState.restoring || syncQueued) return;
    syncQueued = true;
    queueMicrotask(() => {
      syncQueued = false;
      if (restoreState.restoring || status.value !== 'ready') return;
      const next = appRouteUrl(currentAppRoute());
      if (window.location.pathname + window.location.search !== next) window.history.pushState(null, '', next);
    });
  });
}
